import { useState } from 'react';
import { KeyRound, Mail, Phone, Plus, Star } from 'lucide-react';
import { SystemRole, type VendorContact } from '@peles/shared';
import { EntityList, StatusBadge, type EntityListColumn } from '../common/EntityList';
import { EntityModal } from '../common/EntityModal';
import { SegmentedControl } from '../common/SegmentedControl';
import { UserSearchableSelect } from '../users/UserSearchableSelect';
import { useDialog } from '../../context/DialogContext';
import {
  useAddVendorContact,
  useRemoveVendorContact,
  useSetVendorContactPortalAccess,
  useUpdateVendorContact,
  useVendor,
} from '../../hooks/useVendors';
import { extractApiErrorMessage } from '../../utils/apiError';
import { PhoneLink } from '../common/PhoneLink';

interface VendorContactListProps {
  vendorId: string;
  title?: string;
}

/**
 * A vendor's people — the one contact model (`VendorContact`) that portal
 * access, conversations, the event roster and where a vendor's mail goes all
 * read. One is the primary: mail reaches them first when the vendor has no
 * address of its own.
 */
export function VendorContactList({ vendorId, title = 'Contacts' }: VendorContactListProps) {
  const { data: vendor, isLoading } = useVendor(vendorId);
  const addContact = useAddVendorContact(vendorId);
  const updateContact = useUpdateVendorContact(vendorId);
  const removeContact = useRemoveVendorContact(vendorId);
  const portalAccess = useSetVendorContactPortalAccess(vendorId);
  const { confirm, alert } = useDialog();

  const [adding, setAdding] = useState(false);
  const [addMode, setAddMode] = useState<'existing' | 'new'>('existing');
  const [newUserId, setNewUserId] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('');
  const [notify, setNotify] = useState(true);
  const [open, setOpen] = useState<VendorContact | null>(null);
  const [role, setRole] = useState('');

  const contacts = vendor?.contacts ?? [];

  const failed = (title: string) => (err: unknown) =>
    void alert({
      title,
      message: extractApiErrorMessage(err, 'Please try again.'),
      variant: 'error',
    });

  const canAdd =
    addMode === 'existing' ? !!newUserId : newName.trim() !== '' && newEmail.trim() !== '';

  const add = () => {
    if (!canAdd) return;
    const who =
      addMode === 'existing'
        ? { userId: newUserId }
        : { name: newName.trim(), email: newEmail.trim() };
    addContact.mutate(
      {
        ...who,
        role: newRole.trim() || undefined,
        isPrimary: contacts.length === 0,
      },
      {
        onSuccess: () => {
          setNewUserId('');
          setNewName('');
          setNewEmail('');
          setNewRole('');
          setAdding(false);
        },
        onError: failed('Contact not added'),
      },
    );
  };

  /**
   * Whether this person may sign in to the vendor portal. Turning it off
   * switches their whole account off — one login, whichever vendors they work
   * for — so it asks first.
   */
  const setPortal = async (c: VendorContact, enabled: boolean) => {
    if (
      !enabled &&
      !(await confirm({
        title: 'Turn off sign-in',
        message: `${c.userName} will no longer be able to sign in — for ${vendor?.businessName ?? 'this vendor'} or any other vendor they work for.`,
        confirmLabel: 'Turn off',
        variant: 'danger',
      }))
    ) {
      return;
    }
    portalAccess.mutate(
      { userId: c.userId, enabled, ...(enabled ? { notify } : {}) },
      {
        onSuccess: (updated) => setOpen(updated),
        onError: failed(enabled ? 'Sign-in not turned on' : 'Sign-in not turned off'),
      },
    );
  };

  const show = (c: VendorContact) => {
    setOpen(c);
    setRole(c.role ?? '');
  };

  const saveRole = () => {
    if (!open) return;
    updateContact.mutate(
      { userId: open.userId, role: role.trim() || null },
      { onSuccess: () => setOpen(null), onError: failed('Not saved') },
    );
  };

  const setCc = (c: VendorContact, ccOnEmails: boolean) =>
    updateContact.mutate(
      { userId: c.userId, ccOnEmails },
      {
        // Only while the sheet still shows this contact — a late answer must
        // not switch it back from another one opened meanwhile.
        onSuccess: (updated) =>
          setOpen((current) => (current?.userId === updated.userId ? updated : current)),
        onError: failed('Not saved'),
      },
    );

  const makePrimary = (c: VendorContact) =>
    updateContact.mutate(
      { userId: c.userId, isPrimary: true },
      { onSuccess: () => setOpen(null), onError: failed('Not saved') },
    );

  const remove = async (c: VendorContact) => {
    const ok = await confirm({
      title: 'Remove contact',
      message: `Remove ${c.userName} as a contact of ${vendor?.businessName ?? 'this vendor'}? They lose access to its events in the portal.`,
      confirmLabel: 'Remove',
      variant: 'danger',
    });
    if (!ok) return;
    removeContact.mutate(c.userId, {
      onSuccess: () => setOpen(null),
      onError: failed('Not removed'),
    });
  };

  const isOffice = (c: VendorContact) =>
    c.accountRole === SystemRole.ADMIN || c.accountRole === SystemRole.PLANNER;

  const columns: EntityListColumn<VendorContact>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (c) => (
        <div className="min-w-0">
          <p className="flex items-center gap-1 font-medium text-gray-900 text-sm">
            {c.userName}
            {c.isPrimary && (
              <Star size={12} className="text-amber-500 fill-amber-500" aria-label="Primary" />
            )}
            {c.ccOnEmails && <StatusBadge label="CC" color="purple" />}
          </p>
          <p className="text-xs text-gray-500 truncate">{c.userEmail}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (c) =>
        c.role ? (
          <StatusBadge label={c.role} color="blue" />
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'portal',
      header: 'Portal',
      render: (c) =>
        isOffice(c) ? (
          <span className="text-xs text-gray-400">Office</span>
        ) : c.portalAccess ? (
          <StatusBadge label="Can sign in" color="green" />
        ) : (
          <StatusBadge label="No sign-in" color="gray" />
        ),
    },
    {
      key: 'phone',
      header: 'Phone',
      hideOnMobile: true,
      render: (c) =>
        c.userPhone ? (
          <PhoneLink
            phone={c.userPhone}
            name={c.userName ?? undefined}
            wrapperClassName="text-xs"
            whatsappIconClassName="h-3.5 w-3.5"
          />
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
  ];

  const linkClass =
    'flex items-center gap-2 min-h-11 sm:min-h-0 text-sm text-primary-600 hover:text-primary-700';

  return (
    <>
      <EntityList
        items={contacts}
        columns={columns}
        getKey={(c) => c.userId}
        isLoading={isLoading}
        onRowClick={show}
        title={title}
        titleActions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="min-h-11 sm:min-h-9 flex items-center gap-1.5 px-3 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
          >
            <Plus size={16} />
            Add contact
          </button>
        }
        emptyMessage="No contacts yet. A contact you let sign in sees what you share with this vendor on each event."
      />

      {adding && (
        <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <SegmentedControl
            ariaLabel="Who to add"
            options={[
              { id: 'existing', label: 'Someone in the system' },
              { id: 'new', label: 'A new person' },
            ]}
            value={addMode}
            onChange={setAddMode}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {addMode === 'existing' ? (
              <div>
                <span className="block text-xs text-gray-500 mb-1">Person</span>
                <UserSearchableSelect
                  value={newUserId}
                  onChange={(v) => setNewUserId(typeof v === 'string' ? v : '')}
                  placeholder="Search people..."
                />
              </div>
            ) : (
              <>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">Name</span>
                  <input
                    type="text"
                    value={newName}
                    maxLength={200}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-500 mb-1">
                    Email (their Google sign-in)
                  </span>
                  <input
                    type="email"
                    value={newEmail}
                    maxLength={320}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </label>
              </>
            )}
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">Role</span>
              <input
                type="text"
                value={newRole}
                maxLength={100}
                onChange={(e) => setNewRole(e.target.value)}
                placeholder="e.g. Owner, Sales, Day-of lead"
                className="w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="min-h-11 sm:min-h-9 px-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={add}
              disabled={!canAdd || addContact.isPending}
              className="min-h-11 sm:min-h-9 px-3 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      <EntityModal
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open?.userName ?? 'Contact'}
        size="sm"
        footer={
          open ? (
            <div className="flex flex-wrap justify-between gap-2 w-full">
              <button
                type="button"
                onClick={() => void remove(open)}
                className="min-h-11 sm:min-h-9 px-3 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
              >
                Remove contact
              </button>
              <button
                type="button"
                onClick={saveRole}
                disabled={updateContact.isPending || role.trim() === (open.role ?? '')}
                className="min-h-11 sm:min-h-9 px-3 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          ) : undefined
        }
      >
        {open && (
          <div className="space-y-4">
            <label className="block">
              <span className="block text-xs text-gray-500 mb-1">Role</span>
              <input
                type="text"
                value={role}
                maxLength={100}
                onChange={(e) => setRole(e.target.value)}
                className="w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </label>
            {open.isPrimary ? (
              <p className="flex items-center gap-1 text-sm text-amber-700">
                <Star size={14} className="fill-amber-500 text-amber-500" /> Primary contact
              </p>
            ) : (
              <button
                type="button"
                onClick={() => makePrimary(open)}
                disabled={updateContact.isPending}
                className="min-h-11 sm:min-h-9 px-3 inline-flex items-center gap-1 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Star size={14} /> Make primary
              </button>
            )}
            <label className="flex min-h-11 sm:min-h-0 items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={open.ccOnEmails}
                disabled={updateContact.isPending}
                onChange={(e) => setCc(open, e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                CC on vendor emails
                <span className="block text-xs text-gray-500">
                  Copied when an email to {vendor?.businessName ?? 'this vendor'} is sent from an
                  event with “CC the vendors’ contacts” ticked.
                </span>
              </span>
            </label>
            <section className="space-y-2 rounded-xl border border-gray-200 p-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <KeyRound size={14} /> Vendor portal
              </p>
              {isOffice(open) ? (
                <p className="text-sm text-gray-600">An office account — they sign in already.</p>
              ) : open.portalAccess ? (
                <>
                  <p className="text-sm text-gray-600">
                    Signs in with Google as {open.userEmail}, and sees what you share with{' '}
                    {vendor?.businessName ?? 'this vendor'} on each event.
                  </p>
                  <button
                    type="button"
                    onClick={() => void setPortal(open, false)}
                    disabled={portalAccess.isPending}
                    className="min-h-11 sm:min-h-9 px-3 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    Turn off sign-in
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Cannot sign in. Let them in with their Google account for {open.userEmail}; they
                    see only what you share with {vendor?.businessName ?? 'this vendor'} on each
                    event.
                  </p>
                  <label className="flex min-h-11 sm:min-h-0 items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notify}
                      onChange={(e) => setNotify(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Email them that they can sign in
                  </label>
                  <button
                    type="button"
                    onClick={() => void setPortal(open, true)}
                    disabled={portalAccess.isPending}
                    className="min-h-11 sm:min-h-9 px-3 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    Let them sign in
                  </button>
                </>
              )}
            </section>
            <div className="space-y-2">
              {open.userEmail && (
                <a href={`mailto:${open.userEmail}`} className={linkClass}>
                  <Mail size={14} /> {open.userEmail}
                </a>
              )}
              {open.userPhone && (
                <PhoneLink
                  phone={open.userPhone}
                  name={open.userName ?? undefined}
                  icon={<Phone size={14} />}
                  className={linkClass}
                />
              )}
            </div>
          </div>
        )}
      </EntityModal>
    </>
  );
}
